// ─── Research Engine Orchestrator ─────────────────────────────────────────────
// Coordinates the full pipeline: generate → compute → chart → persist.
// All results saved to Supabase for cross-session access and chapter injection.

import { supabase } from '../../lib/supabase'
import { generateDataset, datasetToCSV } from './dataGenerator'
import {
  computeDescriptiveStats,
  computeCorrelationMatrix,
  computeOLS,
  computeCronbachAlpha,
  computeOneWayANOVA,
  computeModeration,
} from './statisticsEngine'
import {
  scatterRegressionSVG,
  correlationHeatmapSVG,
  coefficientPlotSVG,
  histogramSVG,
  residualsPlotSVG,
} from './chartEngine'
import { logEvent, EVENT } from '../logger'

// ── Dataset persistence ────────────────────────────────────────────────────────

export async function saveDataset(thesisId, dataset, name = 'Primary Dataset') {
  const csv = datasetToCSV(dataset)
  const { data, error } = await supabase
    .from('thesis_research_datasets')
    .insert({
      thesis_id: thesisId,
      name,
      variables_config: dataset.variables,
      csv_data: csv,
      row_count: dataset.rows.length,
    })
    .select('id')
    .single()

  if (error) throw new Error(`Dataset save failed: ${error.message}`)
  return data.id
}

export async function loadDataset(thesisId) {
  const { data, error } = await supabase
    .from('thesis_research_datasets')
    .select('*')
    .eq('thesis_id', thesisId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return data
}

export async function listDatasets(thesisId) {
  const { data } = await supabase
    .from('thesis_research_datasets')
    .select('id, name, row_count, variables_config, created_at')
    .eq('thesis_id', thesisId)
    .order('created_at', { ascending: false })
  return data || []
}

// ── Result persistence ─────────────────────────────────────────────────────────

export async function saveResult(thesisId, datasetId, analysisType, label, resultJson) {
  const { data, error } = await supabase
    .from('thesis_research_results')
    .insert({
      thesis_id: thesisId,
      dataset_id: datasetId,
      analysis_type: analysisType,
      label,
      result_json: resultJson,
    })
    .select('id')
    .single()

  if (error) throw new Error(`Result save failed: ${error.message}`)
  return data.id
}

export async function loadResults(thesisId) {
  const { data } = await supabase
    .from('thesis_research_results')
    .select('*')
    .eq('thesis_id', thesisId)
    .order('created_at', { ascending: false })
  return data || []
}

// ── Chart persistence ──────────────────────────────────────────────────────────

export async function saveChart(thesisId, resultId, chartType, title, svgContent) {
  const { data, error } = await supabase
    .from('thesis_research_charts')
    .insert({
      thesis_id: thesisId,
      result_id: resultId,
      chart_type: chartType,
      title,
      svg_content: svgContent,
    })
    .select('id')
    .single()

  if (error) throw new Error(`Chart save failed: ${error.message}`)
  return data.id
}

export async function loadCharts(thesisId) {
  const { data } = await supabase
    .from('thesis_research_charts')
    .select('*')
    .eq('thesis_id', thesisId)
    .order('created_at', { ascending: false })
  return data || []
}

// ── Full pipeline ──────────────────────────────────────────────────────────────

/**
 * Generate dataset + run full statistical analysis + generate charts.
 * Returns all results for display and saves to Supabase.
 *
 * @param {string} thesisId
 * @param {object} config - { variables, sampleSize, dvName, ivNames, analysisTypes }
 */
export async function runFullPipeline(thesisId, config, onProgress) {
  const t0 = Date.now()
  const { variables, sampleSize, dvName, ivNames = [], analysisTypes = ['descriptive', 'correlation', 'ols'] } = config

  const progress = (msg, pct) => onProgress?.({ message: msg, percent: pct })

  // 1. Generate synthetic dataset
  progress('Generating synthetic dataset…', 10)
  const dataset = generateDataset({ variables, sampleSize })
  const { rows } = dataset

  // 2. Save dataset
  progress('Saving dataset…', 20)
  let datasetId = null
  try {
    datasetId = await saveDataset(thesisId, dataset)
  } catch (e) {
    console.warn('[researchEngine] Dataset save failed (proceeding):', e.message)
  }

  const results = {}
  const charts = {}

  // 3. Descriptive statistics
  if (analysisTypes.includes('descriptive')) {
    progress('Computing descriptive statistics…', 30)
    try {
      results.descriptive = computeDescriptiveStats(rows, variables)
      // Histograms for each numeric variable
      charts.histograms = {}
      for (const v of variables) {
        const vals = rows.map(r => r[v.name]).filter(x => x != null && !isNaN(x)).map(Number)
        charts.histograms[v.name] = histogramSVG({ values: vals, label: v.label || v.name, title: `Distribution: ${v.label || v.name}` })
      }
    } catch (e) {
      console.warn('[researchEngine] Descriptive stats failed:', e.message)
    }
  }

  // 4. Correlation matrix
  if (analysisTypes.includes('correlation')) {
    progress('Computing correlation matrix…', 45)
    try {
      results.correlation = computeCorrelationMatrix(rows, variables)
      charts.heatmap = correlationHeatmapSVG({
        matrix: results.correlation.matrix,
        pvalues: results.correlation.pvalues,
        variables: results.correlation.variables,
        title: 'Correlation Matrix',
      })
    } catch (e) {
      console.warn('[researchEngine] Correlation failed:', e.message)
    }
  }

  // 5. OLS Regression
  if (analysisTypes.includes('ols') && dvName && ivNames.length > 0) {
    progress('Running OLS regression…', 60)
    try {
      results.ols = computeOLS(rows, dvName, ivNames)
      charts.coefficients = coefficientPlotSVG({
        coefficients: results.ols.coefficients,
        dvLabel: dvName,
        title: `Regression Coefficients: ${dvName}`,
      })

      // Scatter plots for each significant IV vs DV
      charts.scatters = {}
      for (const iv of ivNames) {
        const xData = rows.filter(r => r[iv] != null && r[dvName] != null).map(r => +r[iv])
        const yData = rows.filter(r => r[iv] != null && r[dvName] != null).map(r => +r[dvName])
        const coef = results.ols.coefficients[iv]
        charts.scatters[iv] = scatterRegressionSVG({
          xData, yData,
          xLabel: iv, yLabel: dvName,
          title: `${iv} → ${dvName}`,
          beta: coef?.beta,
          intercept: results.ols.coefficients['const']?.beta,
          rSquared: results.ols.r_squared,
        })
      }

      // Residuals plot
      charts.residuals = residualsPlotSVG({
        fitted: results.ols.fitted,
        residuals: results.ols.residuals,
      })
    } catch (e) {
      console.warn('[researchEngine] OLS failed:', e.message)
    }
  }

  // 6. Cronbach Alpha (if likert items present)
  if (analysisTypes.includes('cronbach')) {
    const likertVars = variables.filter(v => v.type === 'likert5' || v.type === 'likert7').map(v => v.name)
    if (likertVars.length >= 2) {
      progress('Computing Cronbach alpha…', 75)
      try {
        results.cronbach = computeCronbachAlpha(rows, likertVars)
      } catch (e) {
        console.warn('[researchEngine] Cronbach failed:', e.message)
      }
    }
  }

  // 7. Save results and charts
  progress('Saving results…', 85)
  const savedResultIds = {}
  for (const [type, result] of Object.entries(results)) {
    try {
      const label = type === 'ols' ? result.label : type === 'cronbach' ? result.label : type
      const resultId = await saveResult(thesisId, datasetId, type, label, result)
      savedResultIds[type] = resultId

      // Save associated charts
      const chartsForType = type === 'ols'
        ? { coefficients: charts.coefficients, residuals: charts.residuals, ...Object.fromEntries(Object.entries(charts.scatters || {}).map(([k, v]) => [`scatter_${k}`, v])) }
        : type === 'correlation'
          ? { heatmap: charts.heatmap }
          : type === 'descriptive'
            ? charts.histograms || {}
            : {}

      for (const [chartType, svg] of Object.entries(chartsForType)) {
        if (svg) {
          await saveChart(thesisId, resultId, chartType, chartType, svg).catch(e => {
            console.warn('[researchEngine] Chart save failed:', e.message)
          })
        }
      }
    } catch (e) {
      console.warn(`[researchEngine] Save failed for ${type}:`, e.message)
    }
  }

  await logEvent(EVENT.BUILD_FIGURES, {
    thesisId,
    status: 'OK',
    durationMs: Date.now() - t0,
    metadata: { sampleSize, variables: variables.length, analyses: Object.keys(results) },
  })

  progress('Complete', 100)

  return { dataset, results, charts, datasetId, savedResultIds }
}
